export type Language = 'en' | 'es';

export const translations: Record<Language, Record<string, string>> = {
  en: {
    // Instructions
    instruction1: '1. Copy the account number and open your banking app',
    instruction2: '2. Send exactly VES {amount}',
    instruction3: '3. Return here and confirm the transfer',

    // Transfer details
    copyTransferDetails: 'Copy transfer details',
    phoneNumber: 'Phone number',
    accountHolder: 'Account holder',
    bankName: 'Bank name',
    idNumber: 'ID number (if required)',

    // Status
    waitingForTransfer: "We're waiting for your transfer:",

    // Button
    sentMoney: "I've sent the money",
    processing: 'Processing...',
    timeExpired: 'Time expired',
    paymentConfirmed: 'Payment confirmed',

    // Hint
    sendExactAmount: 'Send the exact amount to avoid delays in processing',

    // Copy
    copy: 'Copy',
    successfullyCopied: 'Successfully copied!',

    // States
    loading: 'Loading payment data...',
    preparingPayment: 'Preparing payment details',
    pleaseWait: 'Please wait a moment.',
    paymentNotFound: 'Payment ID not found',
    dataNotFound: 'Payment data not found',

    // Confirm dialogs
    success: 'Payment confirmed',
    successDescription: 'Your transfer has been successfully received and verified.',
    successReturnHint: 'You can now return to the merchant.',
    expired: 'Payment session expired',
    expiredDescription: 'The payment time has ended and this transfer request is no longer active.',
    expiredHint: 'Please create a new payment request to continue.',

    // Failed page
    failedTitle: 'Payment not confirmed',
    failedDescription: "We couldn't verify your transfer within the expected time.",
    failedHint: 'If money was sent, it may still be processed later or returned by your provider.',
    tryAgain: 'Try again',
    contactSupport: 'Contact support',
    processingPayment: 'Processing payment!',
    processingDescription: 'Contact Customer Support if your transaction is not processed within a few minutes.',
    backToSite: 'Back to site',

    // Pending page
    pendingTitle: 'Payment is being verified',
    pendingDescription: "We're waiting for a response from the payment provider.",
    pendingKeepOpen: 'Please keep this page open.',
    refreshStatus: 'Refresh status',
    backToMerchant: 'Back to merchant',

    // Unavailable page
    unavailableTitle: 'Payment unavailable',
    unavailableDescription: "We couldn't create payment details right now.",

    // Reference input for confirm
    referenceInputHint: 'After completing the payment, enter the Nro. de referencia and click "I\'ve sent the money". This will help us process your transfer as quickly as possible.',
    referenceInputLabel: 'Nro. de referencia',
    referenceInputPlaceholder: 'Last 4 digits (or full number)',
    referenceAccordionTitle: 'Where to find the Nro. de referencia?',
    referenceAccordionItem1: 'On the final screen of your banking app after a successful Pago Móvil payment — in the Comprobante / Recibo.',
    referenceAccordionItem2: 'In Transaction History / Movimientos / Operaciones realizadas.',
    referenceAccordionItem3: 'In the details of the specific Pago Móvil transaction.',
    referenceAccordionItem4: 'In some banks — in the SMS or email confirmation.',
  },
  es: {
    // Instructions
    instruction1: '1. Copia el número de cuenta y abre tu app bancaria',
    instruction2: '2. Envía exactamente VES {amount}',
    instruction3: '3. Regresa aquí y confirma la transferencia',

    // Transfer details
    copyTransferDetails: 'Copiar detalles de la transferencia',
    phoneNumber: 'Número de teléfono',
    accountHolder: 'Titular de la cuenta',
    bankName: 'Nombre del banco',
    idNumber: 'Cédula (si es necesario)',

    // Status
    waitingForTransfer: 'Estamos esperando tu transferencia:',

    // Button
    sentMoney: 'He enviado el dinero',
    processing: 'Procesando...',
    timeExpired: 'Tiempo expirado',
    paymentConfirmed: 'Pago confirmado',

    // Hint
    sendExactAmount: 'Envía la cantidad exacta para evitar retrasos en el procesamiento',

    // Copy
    copy: 'Copiar',
    successfullyCopied: '¡Copiado con éxito!',

    // States
    loading: 'Cargando datos de pago...',
    preparingPayment: 'Preparando detalles de pago',
    pleaseWait: 'Por favor, espere un momento.',
    paymentNotFound: 'ID de pago no encontrado',
    dataNotFound: 'Datos de pago no encontrados',

    // Confirm dialogs
    success: 'Pago confirmado',
    successDescription: 'Tu transferencia ha sido recibida y verificada con éxito.',
    successReturnHint: 'Ahora puedes regresar al comerciante.',
    expired: 'Sesión de pago expirada',
    expiredDescription: 'El tiempo de pago ha terminado y esta solicitud de transferencia ya no está activa.',
    expiredHint: 'Por favor, crea una nueva solicitud de pago para continuar.',

    // Failed page
    failedTitle: 'Pago no confirmado',
    failedDescription: 'No pudimos verificar tu transferencia dentro del tiempo esperado.',
    failedHint: 'Si se envió dinero, aún podría procesarse más tarde o ser devuelto por tu proveedor.',
    tryAgain: 'Intenta de nuevo',
    contactSupport: 'Contactar soporte',
    processingPayment: '¡Procesando el pago!',
    processingDescription: 'Contacta con atención al cliente si tu transacción no se procesa en unos minutos.',
    backToSite: 'Volver al sitio',

    // Pending page
    pendingTitle: 'El pago está siendo verificado',
    pendingDescription: 'Estamos esperando una respuesta del proveedor de pagos.',
    pendingKeepOpen: 'Por favor, mantén esta página abierta.',
    refreshStatus: 'Actualizar estado',
    backToMerchant: 'Volver al comerciante',

    // Unavailable page
    unavailableTitle: 'Pago no disponible',
    unavailableDescription: 'No pudimos crear los detalles de pago en este momento.',

    // Reference input for confirm
    referenceInputHint: 'Después de realizar el pago, ingrese el Nro. de referencia y haga clic en "He enviado el dinero". Esto nos permitirá procesar su transferencia lo antes posible.',
    referenceInputLabel: 'Nro. de referencia',
    referenceInputPlaceholder: 'Últimos 4 dígitos (o número completo)',
    referenceAccordionTitle: '¿Dónde encontrar el Nro. de referencia?',
    referenceAccordionItem1: 'En la pantalla final de la aplicación bancaria después de realizar correctamente el Pago Móvil — en el Comprobante / Recibo.',
    referenceAccordionItem2: 'En Historial de transacciones / Movimientos / Operaciones realizadas.',
    referenceAccordionItem3: 'En los detalles de la operación específica de Pago Móvil.',
    referenceAccordionItem4: 'En algunos bancos — en la confirmación por SMS o correo electrónico.',
  },
};

export type TranslationKey = keyof typeof translations.en;

export const getLanguageFromUrl = (): Language => {
  const urlParams = new URLSearchParams(window.location.search);
  const lang = urlParams.get('lang');

  if (lang === 'en') return 'en';
  return 'es';
};

export const t = (key: string, params?: Record<string, string | number>): string => {
  const lang = getLanguageFromUrl();
  let text = translations[lang][key] || translations.en[key] || key;

  if (params) {
    Object.entries(params).forEach(([paramKey, value]) => {
      text = text.replace(`{${paramKey}}`, String(value));
    });
  }

  return text;
};

export const useTranslation = () => {
  const lang = getLanguageFromUrl();

  const translate = (key: string, params?: Record<string, string | number>): string => {
    let text = translations[lang][key] || translations.en[key] || key;

    if (params) {
      Object.entries(params).forEach(([paramKey, value]) => {
        text = text.replace(`{${paramKey}}`, String(value));
      });
    }

    return text;
  };

  return { t: translate, lang };
};
